import { UserId } from "@pax/api-contract/common";
import { RoomCommit, RoomOp } from "@pax/basemap";
import { PartFrameSchema } from "@pax/preset-server-contract/drafts/v1alpha1";
import { z } from "zod";
import { service } from "./endpoints";
import { RoomSeq, RoomSnapshot } from "./schemas";

export {
  HEARTBEAT,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_SILENCE_MS,
  messagesOf,
  PartJoiner,
} from "@pax/preset-server-contract/drafts/v1alpha1";

export const socketProtocol = `${service.group}/${service.version}` as const;

/** Why the room closed a socket. */
export const ROOM_CLOSE = {
  /** No heartbeat for `HEARTBEAT_SILENCE_MS`. */
  silent: 4001,
  /** The token isn't good for this draft. */
  unauthorized: 4401,
} as const;

const ConnectionId = z.string().min(1).max(64);
const Point = z.tuple([z.number().min(-1000).max(1000), z.number().min(-90).max(90)]);

/** Where someone is and what they're doing with it. It's passed on and never stored. */
const PresenceSchema = z.strictObject({
  /** Their pointer on the world, as longitude and latitude, or null off it. */
  cursor: Point.nullable(),
  tool: z.string().max(32).nullable(),
  /** How far their brush reaches, in radians, while they hold one. */
  brush: z.number().min(0).max(4).nullable(),
  /** The stroke they're painting, as the points it has passed so far. */
  stroke: z.array(Point).max(2000).nullable(),
});
export type Presence = z.infer<typeof PresenceSchema>;

const PeerSchema = z.strictObject({
  connection: ConnectionId,
  userId: UserId,
  name: z.string().min(1),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  state: PresenceSchema.nullable(),
});
export type Peer = z.infer<typeof PeerSchema>;

export const ClientFrameSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("authenticate"),
    token: z.string().min(1),
    protocol: z.literal(socketProtocol),
  }),
  z.strictObject({ type: z.literal("commit"), commit: RoomCommit }),
  z.strictObject({ type: z.literal("presence"), state: PresenceSchema }),
  PartFrameSchema,
]);
export type ClientFrame = z.infer<typeof ClientFrameSchema>;

/** A commit as the room took it: its place in order, and who made it. */
const TakenCommitSchema = z.strictObject({
  seq: RoomSeq,
  id: z.string(),
  userId: UserId,
  label: z.string(),
  ops: z.array(RoomOp),
});
export type TakenCommit = z.infer<typeof TakenCommitSchema>;

export const ServerFrameSchema = z.discriminatedUnion("type", [
  /** The draft as it is, who the client is, and everyone else in it. */
  z.strictObject({
    type: z.literal("welcome"),
    room: RoomSnapshot,
    frozen: z.boolean(),
    you: z.strictObject({ connection: ConnectionId, userId: UserId }),
    peers: z.array(PeerSchema),
  }),
  /** A commit the room took, the sender's own included, which is its acknowledgement. */
  z.strictObject({ type: z.literal("commit"), commit: TakenCommitSchema }),
  /** A commit of the client's the room couldn't take, and why. */
  z.strictObject({ type: z.literal("rejected"), id: z.string(), reason: z.string() }),
  z.strictObject({ type: z.literal("peers"), peers: z.array(PeerSchema) }),
  z.strictObject({ type: z.literal("presence"), connection: ConnectionId, state: PresenceSchema }),
  /** It stopped taking commits while it's published. */
  z.strictObject({ type: z.literal("frozen") }),
  /** The publication failed, and why: it takes commits again. */
  z.strictObject({ type: z.literal("thawed"), problem: z.string() }),
  /** The draft is published: it's that version now, and its room takes no more commits. */
  z.strictObject({ type: z.literal("published"), versionId: z.uuid() }),
  PartFrameSchema,
]);
export type ServerFrame = z.infer<typeof ServerFrameSchema>;
